import { useEffect, useState } from "react";
import { CalendarClock, Loader2, Receipt, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { billingHistory } from "../lib/api";

const day = (iso) => new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "long", year: "numeric" });
const money = (n) => `R${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PURPOSE = { trial: "Pro free trial", subscription: "Pro subscription", live_test: "PayFast live test", school: "School / homeschool" };
const PROVIDER = { payfast: "PayFast", paystack: "Paystack" };

const PlanSummary = ({ h, onPlan }) => {
  const label = h.plan !== "pro" ? "Free plan" : h.via_school ? "Pro (through your school)" : h.on_trial ? "Pro free trial" : `Pro · ${h.interval === "year" ? "yearly" : "monthly"}`;
  return (
    <div data-testid="billing-plan-card" className="what-plan">
      <p className="font-mono text-xs uppercase tracking-[0.16em] text-[#D4FF3A] flex items-center gap-1.5"><Sparkles size={12} /> Your plan</p>
      <p data-testid="billing-plan-name" className="font-display text-2xl font-bold text-white mt-2">{label}</p>
      {h.provider && h.plan === "pro" && <p className="text-xs text-slate-500 mt-1">Paid with {PROVIDER[h.provider] || h.provider}</p>}
      {h.next_billing_date ? (
        <p data-testid="billing-next-date" className="text-sm text-slate-300 mt-3 flex items-center gap-2">
          <CalendarClock size={14} className="text-[#D4FF3A]" />
          {h.on_trial ? "Trial ends and first payment" : "Next payment"}: <span className="text-white">{day(h.next_billing_date)}</span>
          {h.next_amount && <span className="text-slate-400">· {money(h.next_amount)}</span>}
        </p>
      ) : h.plan === "pro" ? (
        <p data-testid="billing-next-date" className="text-sm text-slate-400 mt-3">{h.via_school ? "Your school pays for your plan." : h.live_test ? "Test payment only, so nothing more will be charged." : "No upcoming payment."}</p>
      ) : (
        <button data-testid="billing-upgrade-btn" onClick={onPlan} className="what-btn-primary mt-4">See Pro plans</button>
      )}
    </div>
  );
};

const Payments = ({ list }) => (
  <div className="mt-5">
    <p className="font-display font-semibold text-white flex items-center gap-2"><Receipt size={15} /> Payments</p>
    {list.length === 0 ? (
      <p data-testid="billing-no-payments" className="text-sm text-slate-500 mt-2">No payments yet.</p>
    ) : (
      <ul data-testid="billing-payments" className="mt-3 divide-y divide-white/[0.06]">
        {list.map((p, i) => (
          <li key={i} data-testid={`billing-payment-${i}`} className="py-3 flex items-center justify-between gap-4 text-sm">
            <div>
              <p className="text-slate-200">{p.note || PURPOSE[p.purpose] || "Payment"}</p>
              <p className="text-xs text-slate-500">{day(p.paid_at)} · {PROVIDER[p.provider] || p.provider}</p>
            </div>
            <span className="font-mono text-white">{money(p.amount)}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const BillingDialog = ({ open, onOpenChange, onPlan }) => {
  const [h, setH] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (open) { setError(false); billingHistory().then(setH).catch(() => setError(true)); }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="billing-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#0E131D] border-white/[0.08] text-slate-200 p-8 font-['Plus_Jakarta_Sans',sans-serif]">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-white">Billing & payments</DialogTitle>
          <DialogDescription className="text-slate-400">Your plan, your next billing date and every payment.</DialogDescription>
        </DialogHeader>
        {error ? <p className="text-sm text-red-400">Couldn't load your billing details.</p>
          : !h ? <Loader2 className="animate-spin text-slate-500 mx-auto my-8" />
          : <><PlanSummary h={h} onPlan={onPlan} /><Payments list={h.payments} /></>}
      </DialogContent>
    </Dialog>
  );
};
